import React, { useMemo, useState, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { parseISO, differenceInSeconds, isValid } from 'date-fns';
import { calculateCompetitionProgress } from '../../utils/competitionProgress';

/**
 * CompetitionProgress displays either:
 * - A countdown before competition starts
 * - A circular progress indicator during competition
 * - A checkmark when complete
 */
const CompetitionProgress = ({
  sessions,
  gymnasts,
  gender = 'F',
  startDate,
  endDate,
  size = 'large'
}) => {
  const [countdown, setCountdown] = useState('');
  const [competitionState, setCompetitionState] = useState('loading'); // 'upcoming', 'in_progress', 'completed'

  // Determine competition state based on dates
  useEffect(() => {
    if (!startDate) {
      setCompetitionState('in_progress'); // If no dates, show progress
      return;
    }

    const updateState = () => {
      try {
        const now = new Date();
        const start = parseISO(startDate);
        const end = endDate ? parseISO(endDate) : null;

        if (!isValid(start)) {
          setCompetitionState('in_progress');
          return;
        }

        if (end && now > end) {
          setCompetitionState('completed');
          setCountdown('');
          return;
        }

        if (now >= start) {
          setCompetitionState('in_progress');
          setCountdown('');
          return;
        }

        // Competition hasn't started - show countdown
        setCompetitionState('upcoming');
        const diffSeconds = differenceInSeconds(start, now);
        const days = Math.floor(diffSeconds / (24 * 60 * 60));
        const hours = Math.floor((diffSeconds % (24 * 60 * 60)) / (60 * 60)).toString().padStart(2, '0');
        const minutes = Math.floor((diffSeconds % (60 * 60)) / 60).toString().padStart(2, '0');
        const seconds = Math.floor(diffSeconds % 60).toString().padStart(2, '0');

        setCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      } catch (error) {
        console.error('Error updating competition state:', error);
        setCompetitionState('in_progress');
      }
    };

    updateState();

    // Only set interval if competition hasn't started yet
    const now = new Date();
    const start = parseISO(startDate);
    if (isValid(start) && now < start) {
      const timer = setInterval(updateState, 1000);
      return () => clearInterval(timer);
    }
  }, [startDate, endDate]);

  const progress = useMemo(
    () => calculateCompetitionProgress(sessions, gymnasts, gender),
    [sessions, gymnasts, gender]
  );

  const { percentage, completedSessions, totalSessions, status } = progress;

  // Size configurations - use circleSize as the canonical height for alignment
  // Small circleSize (24px) matches h6 lineHeight so captions align with other stat labels
  const config = size === 'small'
    ? {
        circleSize: 24,
        mainAreaHeight: 24,
        fontSize: '0.55rem',
        thickness: 3,
        iconSize: 20,
        captionSize: '0.65rem',
        mainVariant: 'h6',
        mainFontWeight: 600,
        mainMb: 0,
        mainLineHeight: 1
      }
    : {
        circleSize: 48,
        mainAreaHeight: 48,
        fontSize: '0.875rem',
        thickness: 4,
        iconSize: 34,
        captionSize: undefined,
        mainVariant: 'h4',
        mainFontWeight: 'normal',
        mainMb: 0.5,
        mainLineHeight: undefined
      };

  // Before competition starts - show countdown
  if (competitionState === 'upcoming' && countdown) {
    const days = countdown.includes('d ') ? parseInt(countdown.split('d ')[0]) : 0;

    // When less than 1 day, show hours instead to maintain 2-row layout
    let displayValue;
    let displayLabel;

    if (days > 0) {
      displayValue = days;
      displayLabel = 'Days Left';
    } else {
      // Extract hours from the countdown string (format: "0d 05h 30m 20s")
      const timePart = countdown.split('d ')[1] || '';
      const hoursMatch = timePart.match(/(\d+)h/);
      const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
      displayValue = hours;
      displayLabel = 'Hours Left';
    }

    return (
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant={config.mainVariant}
          sx={{
            color: 'text.primary',
            fontWeight: config.mainFontWeight,
            ...(config.mainLineHeight && { lineHeight: config.mainLineHeight }),
            ...(config.mainMb && { mb: config.mainMb })
          }}
        >
          {displayValue}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            ...(config.captionSize ? { fontSize: config.captionSize } : {}),
            whiteSpace: 'nowrap'
          }}
        >
          {displayLabel}
        </Typography>
      </Box>
    );
  }

  // Competition complete (by date or by scores)
  if (competitionState === 'completed' || status === 'complete') {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <CheckCircleIcon
          sx={{
            fontSize: config.iconSize,
            color: 'success.main',
            ...(config.mainMb && { mb: config.mainMb })
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ...(config.captionSize ? { fontSize: config.captionSize } : {}) }}
        >
          Complete
        </Typography>
      </Box>
    );
  }

  // Competition in progress - show percentage matching other stat columns
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography
        variant={config.mainVariant}
        sx={{
          fontWeight: config.mainFontWeight,
          ...(config.mainLineHeight && { lineHeight: config.mainLineHeight }),
          ...(config.mainMb && { mb: config.mainMb })
        }}
      >
        {percentage}%
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={config.captionSize ? { fontSize: config.captionSize } : undefined}
      >
        {completedSessions}/{totalSessions} Sessions
      </Typography>
    </Box>
  );
};

export default React.memo(CompetitionProgress);
